import {
  Set as ISet,
} from 'immutable';
import {
  Accessor,
  Component,
  createMemo,
  For,
  JSXElement,
  Setter,
  Show,
  useContext,
} from 'solid-js';
import {
  Dropdown,
  FaIcon,
} from './basic';
import {
  LocalizationContext,
} from './localization';

export const allTags = (() => {
  const tags = {
    os: {
      options: {
        windows: {
          icon: {
            brands: true,
            windows: true,
          },
        },
        linux: {
          icon: {
            brands: true,
            linux: true,
          },
        },
        macos: {
          icon: {
            brands: true,
            apple: true,
          },
        },
        android: {
          icon: {
            brands: true,
            android: true,
          },
        },
        ios: {
          icon: {
            brands: true,
            apple: true,
          },
        },
        web: {
          icon: {
            brands: true,
            html5: true,
          },
        },
      },
    },
    arch: {
      options: {
        x86: {
          icon: {
            solid: true,
            microchip: true,
          },
        },
        arm: {
          icon: {
            solid: true,
            microchip: true,
          },
        },
      },
    },
    bits: {
      options: {
        '32': {
        },
        '64': {
        },
      },
    },
    packaging: {
      options: {
        installer: {
          icon: {
            solid: true,
            'bars-progress': true,
          }
        },
        deb: {
          icon: {
            solid: true,
            'box-archive': true,
          },
        },
        rpm: {
          icon: {
            solid: true,
            'box-archive': true,
          },
        },
        flatpak: {
          icon: {
            solid: true,
            'box-archive': true,
          },
        },
      },
    },
    component: {
      options: {
        client: {
          icon: {
            solid: true,
            laptop: true,
          },
        },
        server: {
          icon: {
            solid: true,
            server: true,
          },
        },
      },
    },
    runtime: {
      options: {
        nodejs: {
          icon: {
            brands: true,
            'node-js': true,
          },
        },
        java: {
          icon: {
            brands: true,
            java: true,
          },
        },
      },
    },
    distro: {
      options: {
        ubuntu: {
          icon: {
            brands: true,
            ubuntu: true,
          },
        },
        fedora: {
          icon: {
            brands: true,
            fedora: true,
          },
        },
        arch: {
          icon: {
            brands: true,
            linux: true,
          },
        },
        nixos: {
          icon: {
            brands: true,
            linux: true,
          },
        },
      },
      requires: 'os:linux',
    },
  };

  let i = 0;
  for(const tag in tags) {
    (tags as any)[tag].order = i++;
    let j = 0;
    for(const option in (tags as any)[tag].options) {
      (tags as any)[tag].options[option].order = j++;
    }
  }

  return tags as any as {
    [Tag in keyof typeof tags]: {
      options: {
        [Option in keyof (typeof tags)[Tag]["options"]]: {
          icon?: {
            [icon: string]: boolean;
          };
          order: number;
        };
      };
      requires?: string;
      order: number;
    };
  };
})();
export type Tag = keyof typeof allTags;

export const compareTags = (a: string, b: string) => {
  const [tagA, optionA] = a.split(':');
  const [tagB, optionB] = b.split(':');
  const ai = allTags[tagA as Tag].order!;
  const bi = allTags[tagB as Tag].order!;
  if(ai != bi) return ai - bi;
  return (allTags[tagA as Tag].options as any)[optionA!].order - (allTags[tagB as Tag].options as any)[optionB!].order;
};

export const getTagDesc = (tag: string) => {
  const [tagName, tagValue] = tag.split(':');
  return (allTags[tagName as Tag].options as any)[tagValue!];
};

export const Tags: Component<{
  tags: ISet<string>;
  showIcons?: boolean;
  inline?: boolean;
  showCodes?: boolean;
  children?: JSXElement;
}> = (props) => {
  const { t } = useContext(LocalizationContext)!;

  return (
    <div classList={{
      tags: true,
      inline: props.inline,
    }}>
      <For each={props.tags.toArray().sort(compareTags)} fallback={<div class="empty">{t('package.tags.empty')}</div>}>{(tag) => {
        const icon = createMemo(() => getTagDesc(tag).icon);
        return (
          <div title={props.showCodes ? tag : undefined}>
            <Show when={(props.showIcons ?? true) && icon()}><FaIcon {...icon()} /></Show>
            <div>{t(`package.tag.${tag}`)}</div>
          </div>
        );
      }}</For>
      {props.children}
    </div>
  );
};

export const TagsEditor: Component<{
  tags: Accessor<ISet<string>>;
  setTags: Setter<ISet<string>>;
  compact: boolean;
}> = (props) => {
  const { t } = useContext(LocalizationContext)!;

  const onTagSelect = (key: string) => {
    props.setTags((tags) => tags.has(key) ? tags.delete(key) : tags.add(key));
    return true;
  };

  return props.compact
  ? <div class="buttons">{
      Object.entries(allTags).map(([tagName, tagDesc]) =>
        <Show when={!tagDesc.requires || props.tags().has(tagDesc.requires)}>
          <Dropdown classList={{
            dropdown_tags: true,
          }} title={t(`package.tag.${tagName}`)} items={
            Object.entries(tagDesc.options).map(([tagOption, tagOptionDesc]) => ({
              key: `${tagName}:${tagOption}`,
              title:
                <div>
                  <Show when={tagOptionDesc.icon}><FaIcon {...tagOptionDesc.icon} inline /></Show>
                  {t(`package.tag.${tagName}:${tagOption}`)}
                </div>,
              icon: <FaIcon item_start inline regular {...{ [props.tags().has(`${tagName}:${tagOption}`) ? 'square-check' : 'square']: true, }} />,
            }))
          } onSelect={onTagSelect} />
        </Show>
      )
    }</div>
  : <div class="tags_editor">{
      Object.entries(allTags).map(([tagName, tagDesc]) =>
        <Show when={!tagDesc.requires || props.tags().has(tagDesc.requires)}>
          <div>
            <header title={tagName}>{t(`package.tag.${tagName}`)}</header>
            {Object.entries(tagDesc.options).map(([tagOption, tagOptionDesc]) =>
              <label title={`${tagName}:${tagOption}`}>
                <input type="checkbox" checked={props.tags().has(`${tagName}:${tagOption}`)} onClick={() => onTagSelect(`${tagName}:${tagOption}`)} />
                <Show when={tagOptionDesc.icon}><FaIcon {...tagOptionDesc.icon} inline /></Show>
                {t(`package.tag.${tagName}:${tagOption}`)}
              </label>
            )}
          </div>
        </Show>
      )
    }</div>
  ;
};
